// ============================================================
// NotificationsBell.tsx
// Bell icon for the nav bar. Reads the user's real Notification feed
// (GET /api/me/notifications) — individually-addressable rows with true
// per-item read state (readAt) and deep-links. Replaces the old
// localStorage-last-read-over-the-activity-feed model.
//
// Unread = server-provided count of rows with readAt === null (badged as
// a red COUNT, capped "9+"). Clicking a row marks it read (optimistic) and deep-links to
// its target (task / case / day). "Mark all as read" clears every unread.
//
// Stage 1 of notifications: the feed is populated by the existing
// event-notifies (task assigned/completed, case assigned — see
// lib/notifications.createNotification), so the bell has real content
// immediately; reminders + deadline/task-due scans land here in later
// stages.
// ============================================================
import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  listNotificationsApi,
  markNotificationReadApi,
  markAllNotificationsReadApi,
} from '../../api/casevoApi'
import { useCasevoStore } from '../../store/useCasevoStore'
import { navigateTo } from '../../navigation/navigate'
import type { AppNotification } from '../../types/notification.types'
import NotificationsPanel from './NotificationsPanel'
import RailTooltip from '../shared/RailTooltip'
import { flushAllAutoSaves } from '../../hooks/flushAllAutoSaves'
import { openBillById } from '../../utils/openBill'
import { NO_LIMITATION_PARAM, NO_LIMITATION_VALUE, noLimitationLinkAssignee } from '../../utils/missingLimitation'
import { playNotificationChime } from '../../utils/notificationChime'
import { DEFAULT_NOTIFICATION_PREFERENCES } from '../../types/notifications.types'
import { useUserChange } from '../../utils/liveEvents'
import { OPENS_ESIGN_TYPES, parseStatementLink, requestStatementFocus } from '../settlement/statement/statementFocus'

// First page only — the bell is a glanceable summary. max-h scroll
// handles overflow; a future "see all" could paginate.
const FEED_LIMIT = 20

// PERF: the bell lives in AppHeader, which every screen renders its own copy
// of — so each screen change remounted it and re-fetched the feed. The last
// result is kept here (per signed-in user) so a remount paints it instantly;
// it re-fetches on mount only when that copy is older than BELL_FRESH_MS. The
// 45s poll, the on-visible/focus refetch and the open-panel refetch below are
// unchanged, so freshness is the same as before.
const BELL_FRESH_MS = 30_000
let bellCache: { userId: string; data: AppNotification[]; unread: number; at: number } | null = null

// ★ New-notification alerts — an in-app toast and / or a chime when a NEW
// unread notification arrives (per-user notificationPreferences:
// popupOnNotification default ON, soundOnNotification default OFF). In-app
// only: not email, not a browser / OS notification. "New" = an unread id this
// user's session hasn't seen yet: the first load just records what's there
// (so opening the app never alerts for old notifications), and the set lives
// at module level so a per-view remount of the bell doesn't re-alert.
let seenIds: { userId: string; ids: Set<string> } | null = null
const takeNew = (userId: string, list: AppNotification[]): AppNotification[] => {
  if (!seenIds || seenIds.userId !== userId) {
    seenIds = { userId, ids: new Set(list.map((n) => n.id)) }
    return []
  }
  const fresh = list.filter((n) => !n.readAt && !seenIds!.ids.has(n.id))
  for (const n of list) seenIds.ids.add(n.id)
  return fresh
}
const TOAST_MS = 6000

const NotificationsBell: React.FC = () => {
  const userId = useCasevoStore((s) => s.authUser?.id ?? null)
  const cached = bellCache && bellCache.userId === userId ? bellCache : null
  const [isOpen, setIsOpen] = useState(false)
  const [notifications, setNotifications] = useState<AppNotification[]>(cached?.data ?? [])
  const [unreadCount, setUnreadCount] = useState(cached?.unread ?? 0)
  const buttonRef = useRef<HTMLButtonElement>(null)
  // The in-app toast for a new notification (+ how many more arrived with it).
  const [toast, setToast] = useState<{ n: AppNotification; more: number } | null>(null)
  useEffect(() => {
    if (!toast) return
    const t = window.setTimeout(() => setToast(null), TOAST_MS)
    return () => window.clearTimeout(t)
  }, [toast])
  // The bell gives way to nothing: any interaction with it clears the toast.
  const clearToast = useCallback(() => setToast(null), [])
  const openRef = useRef(isOpen)
  openRef.current = isOpen
  useEffect(() => { if (isOpen) clearToast() }, [isOpen, clearToast])

  // Load the user's notifications (page 1) + unread count.
  const loadNotifications = useCallback(async () => {
    try {
      const res = await listNotificationsApi(1, FEED_LIMIT)
      setNotifications(res.data)
      setUnreadCount(res.unreadCount)
      if (userId) {
        bellCache = { userId, data: res.data, unread: res.unreadCount, at: Date.now() }
        // New arrivals → the toast / chime the user has switched on (read fresh
        // from the store so a just-flipped toggle applies at once).
        const fresh = takeNew(userId, res.data)
        if (fresh.length) {
          const prefs = { ...DEFAULT_NOTIFICATION_PREFERENCES, ...(useCasevoStore.getState().authUser?.notificationPreferences ?? {}) }
          // (No toast while the bell is open — its list already shows them.)
          if (prefs.popupOnNotification && !openRef.current) setToast({ n: fresh[0], more: fresh.length - 1 })
          if (prefs.soundOnNotification) playNotificationChime()
        }
      }
    } catch {
      // Silently fail — notifications are non-critical.
    }
  }, [userId])
  // Local changes (mark read / dismiss) keep the shared copy in step.
  useEffect(() => {
    if (userId && bellCache && bellCache.userId === userId) bellCache = { ...bellCache, data: notifications, unread: unreadCount }
  }, [userId, notifications, unreadCount])

  // Fetch on mount (unless this user's copy is fresh); re-fetch when the panel
  // opens (latest data).
  useEffect(() => {
    const c = bellCache && bellCache.userId === userId ? bellCache : null
    if (c && Date.now() - c.at < BELL_FRESH_MS) return
    loadNotifications()
  }, [loadNotifications, userId])
  useEffect(() => {
    if (isOpen) loadNotifications()
  }, [isOpen, loadNotifications])
  // (D12 live updates) A notification arrived / was read in another tab
  // (user-changed ['notifications']) — or a catch-up after a reconnect / the
  // tab coming back → the badge and the list re-read at once, no refresh.
  useUserChange(['notifications'], () => { void loadNotifications() }, !!userId)

  // Keep the feed fresh WITHOUT a manual refresh, robust to two things:
  //
  //  1. BACKGROUND-TAB THROTTLING. Browsers throttle (and often pause)
  //     setInterval in a hidden tab, so a plain 45s poll may never fire
  //     while Casevo sits in a background tab — exactly when you'd set a
  //     reminder then switch away to wait for it. The visibility/focus
  //     refetch below fires the instant you return to the tab, so the
  //     bell is current the moment you look at it.
  //  2. PER-VIEW REMOUNT. The bell lives in AppHeader, which every view
  //     renders its own copy of, so navigating remounts this component
  //     and restarts the interval from zero. The interval still covers
  //     the case where you sit on ONE view long enough; the on-visible
  //     refetch covers everything else (nav, tab-switch, wake-from-idle).
  //
  // loadNotifications already updates both the list + the unread count,
  // and is a stable useCallback, so the interval is created once per mount
  // and both listeners are cheap page-1 (20-row) queries.
  // (Batch D) The poll SKIPS while the tab is hidden (no background traffic),
  // and the return-to-tab refetch is debounced — visibilitychange AND focus
  // both fire on return, which used to double-fetch.
  useEffect(() => {
    let last = 0
    const refresh = (): void => {
      const now = Date.now()
      if (now - last < 2000) return
      last = now
      loadNotifications()
    }
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh()
    }, 45000)
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', refresh)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', refresh)
    }
  }, [loadNotifications])

  // Deep-link a notification to its target, reusing the existing nav
  // primitives (mirrors the sidebar's takeover-then-navigate pattern).
  const routeNotification = useCallback(async (n: AppNotification) => {
    if (!n.linkKind || !n.linkId) return
    // ★ Bill alerts (approval request / approved / sent back — linkId = the
    // bill): straight to that bill (utils/openBill — loads it FIRST, then
    // navigates, so a failed load never strands the user on the case list).
    if (n.linkKind === 'bill') { await openBillById(n.linkId); return }
    // ★ (Audit) Persist any pending edit BEFORE closing the open record —
    // closeAllTakeovers clears the case, cancelling the 1s autosave timer, and
    // reopening the SAME case then replaced the unsaved edit with the server copy.
    await flushAllAutoSaves()
    const id = n.linkId
    // One history step via the navigation layer (Back returns to where the
    // bell was clicked); the intents are the destination's one-shot hand-offs.
    switch (n.linkKind) {
      case 'task':
        // Open the Tasks view + deep-link into THIS task's detail (the
        // pendingTaskId consumer opens its form once the list loads /
        // via fetch-by-id).
        await navigateTo({ view: 'tasks' }, { skipFlush: true, intents: (s) => { s.setPendingTaskId(id); s.setTaskOpenedFrom(null) } })
        break
      case 'case':
        // Open the case detail (the ViewingPane skeleton shows while it loads).
        void navigateTo({ view: 'caseDetail', caseId: id }, { skipFlush: true, intents: (s) => s.setCaseOpenedFrom('caseList') }).catch((err) => {
          // eslint-disable-next-line no-console
          console.error('[NotificationsBell] Failed to open case:', err)
        })
        break
      case 'day':
        // No per-day deep-link target yet (Stage 1) — open the calendar.
        await navigateTo({ view: 'calendar' }, { skipFlush: true })
        break
      case 'appointment':
        // Appointment-invite → open the calendar + that appointment's VIEW.
        // CalendarView consumes pendingCalendarAppointmentId on mount / change.
        await navigateTo({ view: 'calendar' }, { skipFlush: true, intents: (s) => s.setPendingCalendarAppointmentId(id) })
        break
      case 'caseList':
        // The daily "No limitation date" nudge (several cases): the Cases list
        // on that filter, status reset to all and the assignee filter the
        // nudge counted with — linkId "no-limitation:me" (a fee earner's own
        // cases) or "no-limitation:all" (some came to an admin as nobody's);
        // a bare "no-limitation" (older rows) → all. Same as the email's link.
        const assignee = noLimitationLinkAssignee(id)
        if (!assignee) break
        // ★ (Audit) One-shot params — the remembered filters are left alone.
        await navigateTo({ view: 'caseList', listTab: 'all', params: { [NO_LIMITATION_PARAM]: NO_LIMITATION_VALUE, assignee } }, { skipFlush: true })
        break
      case 'statement': {
        // (D12) The e-signature alerts — linkId "<caseId>:<statementId>" →
        // the case's Settlement section on that statement; the
        // colleague-confirm ones also open its Send window on e-signature
        // (StatementPanel takes the hand-off once its statements load).
        const link = parseStatementLink(id)
        if (!link) break
        requestStatementFocus({ ...link, openEsign: OPENS_ESIGN_TYPES.has(n.type) })
        await navigateTo({ view: 'caseDetail', caseId: link.caseId, caseSection: 'settlement' }, {
          skipFlush: true,
          intents: (s) => { s.setCaseInitialSection('settlement'); s.setCaseOpenedFrom('caseList') },
        }).catch((err) => {
          // eslint-disable-next-line no-console
          console.error('[NotificationsBell] Failed to open the statement’s case:', err)
        })
        break
      }
      case 'member':
        // Email-change request → Admin → Team, that member's modal (only
        // admins receive these; guard anyway — AdminPage gates on role too).
        if (useCasevoStore.getState().authUser?.role !== 'admin') break
        await navigateTo({ view: 'admin', params: { section: 'team' } }, { skipFlush: true, intents: (s) => s.setPendingAdminMemberId(id) })
        break
    }
  }, [])

  // Click a notification row: optimistically mark it read (readAt now +
  // decrement unread), persist, close the panel, then navigate.
  const handleSelect = useCallback(
    async (n: AppNotification) => {
      if (!n.readAt) {
        setNotifications((prev) =>
          prev.map((x) => (x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x)),
        )
        setUnreadCount((c) => Math.max(0, c - 1))
        void markNotificationReadApi(n.id).catch(() => {
          // Non-fatal — the next load reconciles the true state.
        })
      }
      setIsOpen(false)
      void routeNotification(n)
    },
    [routeNotification],
  )

  // Mark every unread notification read (optimistic + persist).
  const handleMarkAllRead = useCallback(() => {
    const nowIso = new Date().toISOString()
    setNotifications((prev) => prev.map((x) => (x.readAt ? x : { ...x, readAt: nowIso })))
    setUnreadCount(0)
    void markAllNotificationsReadApi().catch(() => {
      // Non-fatal — the next load reconciles.
    })
  }, [])

  const hasUnread = unreadCount > 0

  return (
    // ★ Any bell interaction — hover, keyboard focus or opening it — clears the
    // new-notification toast, so the toast and the bell's own preview /
    // dropdown are never on screen together (opening is also handled by the
    // isOpen effect, and no new toast shows while the bell is open).
    // The toast sits OUTSIDE this wrapper, so hovering the toast itself
    // doesn't count as touching the bell.
    <>
    <div className="relative shrink-0" onMouseEnter={clearToast} onFocusCapture={clearToast}>
      {/* App tooltip (hover + keyboard focus); hidden while the panel is open. */}
      <RailTooltip enabled={!isOpen} placement="below" belowEdgeOf="nav" label="View notifications" className="flex">
      <button
        ref={buttonRef}
        onClick={(e) => {
          setIsOpen((prev) => !prev)
          e.currentTarget.blur()
        }}
        aria-label={hasUnread ? `View notifications (${unreadCount} unread)` : 'View notifications'}
        // Plain icon button — no tile — matching the other topbar icons
        // (Sidebar menu button: text-casevo-light hover:text-white p-2).
        className="relative text-casevo-light hover:text-white transition-colors p-2
                   focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-light rounded"
      >
        {/* Outline bell icon (SVG) */}
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>

        {/* Unread COUNT badge (capped "9+"); none when nothing unread. Sits on
            the icon's top-right shoulder; the 2px ring in the header colour
            cuts it cleanly out of the bell stroke. */}
        {hasUnread && (
          <span
            aria-hidden="true"
            className="absolute top-[-1px] right-[-3px] min-w-[17px] h-[17px] px-[4px] rounded-[9px] bg-[#e5484d] text-white
                       text-[10.5px] font-bold leading-none grid place-items-center tabular-nums
                       shadow-[0_0_0_2px_var(--casevo-header)]"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      </RailTooltip>

      <NotificationsPanel
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        notifications={notifications}
        onSelect={handleSelect}
        onMarkAllRead={handleMarkAllRead}
        hasUnread={hasUnread}
        unreadCount={unreadCount}
        excludeRef={buttonRef}
      />
    </div>
      {/* New-notification toast (popupOnNotification). Click → open it, as
          from the bell; auto-dismisses after TOAST_MS; × to dismiss now. */}
      {toast && (
        <div className="fixed top-[68px] right-4 z-[100] w-[340px] max-w-[calc(100vw-32px)]" role="status" aria-live="polite">
          <div className="flex items-start gap-3 px-4 py-3 rounded-[12px] border border-casevo-border bg-casevo-surface shadow-[0_8px_28px_rgba(16,42,29,0.18)] dark:shadow-[0_8px_28px_rgba(0,0,0,0.45)]">
            <span className="mt-[2px] w-7 h-7 shrink-0 rounded-[8px] grid place-items-center bg-casevo-mint text-casevo-brand dark:text-[#8fd6b0]" aria-hidden="true">
              <svg viewBox="0 0 24 24" width={15} height={15} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg>
            </span>
            <button type="button" className="min-w-0 flex-1 text-left focus:outline-none casevo-focus rounded-sm" onClick={() => { const n = toast.n; setToast(null); void handleSelect(n) }}>
              <b className="block text-[13.5px] font-semibold text-casevo-forest leading-snug">{toast.n.title}</b>
              {toast.n.body && <span className="block text-[12.5px] text-casevo-text-soft mt-[2px] truncate">{toast.n.body}</span>}
              {toast.more > 0 && <span className="block text-[12px] text-casevo-text-soft mt-[2px]">+{toast.more} more in the bell</span>}
            </button>
            <button type="button" aria-label="Dismiss" onClick={() => setToast(null)} className="shrink-0 w-6 h-6 rounded-[6px] grid place-items-center text-casevo-text-soft hover:bg-casevo-mint hover:text-casevo-forest focus:outline-none casevo-focus">
              <svg viewBox="0 0 24 24" width={12} height={12} fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
      )}
    </>
  )
}

export default NotificationsBell
